<template>
  <div class="input-wrapper date-input-wrapper">
    <Datepicker v-model="selectedDate" :range="range" :multi-calendars="multiCalendars" :format="formatDisplayDate"
      :placeholder="placeholder" :enable-time-picker="enableTimePicker" :input-class-name="inputClassName"
      class="date-picker-component" :auto-apply="autoApply" :clearable="clearable" :max-range="maxRange" />
    <span class="input-icon icon-right">
      <img src="./../../public/assets/icons/ic_calendar.svg" alt="Calendar Icon" width="24px" height="24px" />
    </span>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import Swal from "sweetalert2";

// --- Props ---
// Define props to make the component configurable
const props = defineProps({
  // Use modelValue for v-model compatibility
  modelValue: {
    type: Array, // Expecting an array for range picker [startDate, endDate]
    default: null,
  },
  placeholder: {
    type: String,
    default: "DD/MM/YYYY - DD/MM/YYYY",
  },
  range: {
    type: Boolean,
    default: true, // Defaulting based on your original usage
  },
  multiCalendars: {
    type: Boolean,
    default: true, // Defaulting based on your original usage
  },
  enableTimePicker: {
    type: Boolean,
    default: false, // Defaulting based on your original usage
  },
  inputClassName: {
    type: String,
    default: "dp-custom-input", // Defaulting based on your original usage
  },
  autoApply: {
    type: Boolean,
    default: true, // Defaulting based on your original usage
  },
  clearable: {
    type: Boolean,
    default: false, // Defaulting based on your original usage
  },
  maxRange: {
    type: [Number, String],
    default: 31,
  },
  // Add more props if you need to configure other datepicker options
});

// --- Emits ---
// Define the event emitted for v-model updates
const emit = defineEmits(["update:modelValue"]);

// Utility to cap date range to max 31 days
const enforceMaxRange = (value) => {
  if (Array.isArray(value) && value.length === 2 && value[0] && value[1]) {
    const start = new Date(value[0]);
    const end = new Date(value[1]);

    const d1 = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    const d2 = new Date(end.getFullYear(), end.getMonth(), end.getDate());

    const diffTime = Math.abs(d2 - d1);
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
    const maxAllowed = Number(props.maxRange) || 31;

    if (diffDays > maxAllowed) {
      const cappedEnd = new Date(start);
      cappedEnd.setDate(cappedEnd.getDate() + (maxAllowed - 1));

      Swal.fire({
        toast: true,
        position: "top-end",
        icon: "warning",
        title: `Maximum date range is ${maxAllowed} days`,
        showConfirmButton: false,
        showCloseButton: true,
        timer: 3000,
        timerProgressBar: true,
      });

      return [start, cappedEnd];
    }
  }
  return value;
};

// --- Internal State ---
// Local ref to manage the datepicker's state internally
// Initialize it with the initial prop value
const internalDate = ref(enforceMaxRange(props.modelValue));

// --- Computed property for v-model ---
// This computed property makes handling v-model easier.
// It gets the internal value and sets it while emitting the update event.
const selectedDate = computed({
  get: () => internalDate.value,
  set: (value) => {
    const cappedValue = enforceMaxRange(value);
    internalDate.value = cappedValue;
    emit("update:modelValue", cappedValue); // Emit update for the parent
  },
});

// --- Watch for external changes ---
// If the parent component changes the modelValue prop directly, update the internal state
watch(
  () => props.modelValue,
  (newValue) => {
    internalDate.value = enforceMaxRange(newValue);
  }
);

onMounted(() => {
  if (!props.modelValue) {
    const today = new Date();
    const defaultRange = [today, today];
    internalDate.value = defaultRange;
    emit("update:modelValue", defaultRange);
  }
});

// --- Methods ---
// Formatting logic moved from the parent component
const formatDisplayDate = (dateRange) => {
  if (!dateRange || !Array.isArray(dateRange) || dateRange.length !== 2)
    return "";
  const [start, end] = dateRange;
  if (!start || !end) return "";

  // Format to DD/MM/YY
  const formatDate = (d) => {
    if (!(d instanceof Date)) return ""; // Add type check
    const day = d.getDate().toString().padStart(2, "0");
    const month = (d.getMonth() + 1).toString().padStart(2, "0");
    const year = d.getFullYear().toString().slice(-2);
    return `${day}/${month}/${year}`;
  };

  const formattedStart = formatDate(start);
  const formattedEnd = formatDate(end);

  if (!formattedStart || !formattedEnd) return ""; // Handle invalid dates after formatting attempt
  return `${formattedStart} - ${formattedEnd}`;
};
</script>

<style scoped>
/* --- Styles --- */
/* Move relevant styles from the parent component here.
     Use 'scoped' to prevent styles from leaking out,
     or use :deep() if you need to target datepicker internals specifically.
  */

/* Wrapper for positioning icons */
.input-wrapper {
  position: relative;
  display: flex;
  /* To align items if needed */
  align-items: center;
  /* Vertically center icon and input */
}

/* Scoped style */
:deep(.date-picker-component .dp__menu) {
  z-index: 9999 !important;
}

/* Base styling for inputs to match Figma (might be shared, consider a global style or pass class) */
/* NOTE: We reference the prop `inputClassName` in the template, so direct styling
     of '.dp-custom-input' here might be redundant IF that class ONLY defines the styles below.
     However, keeping it ensures the styles apply even if the prop isn't passed. */
.dp-custom-input {
  height: 40px;
  z-index: 1000 !important;

  border-radius: 8px !important;
  border: 1px solid #e2e8f0 !important;
  background-color: #f8fafc !important;
  padding-top: 0 !important;
  padding-bottom: 0 !important;
  font-size: 14px !important;
  color: #4a5568;
  box-sizing: border-box;
  width: 100%;
  transition: border-color 0.2s ease-in-out;
  /* Specific padding for date picker with right icon */
  padding-left: 12px !important;
  padding-right: 40px !important;
}

.dp-custom-input:focus {
  border-color: #4299e1 !important;
  outline: none;
  box-shadow: 0 0 0 1px rgba(66, 153, 225, 0.5);
}

.dp-custom-input::placeholder {
  color: #a0aec0;
  opacity: 1;
}

/* Specific adjustments for Datepicker wrapper */
.date-input-wrapper {
  min-width: 220px;
  /* Adjust as needed for placeholder */
  width: auto;
  /* Or set a specific width if needed */
}

.date-picker-component {
  width: 100%;
  /* Make datepicker fill its wrapper */
}

/* Icon positioning */
.input-icon {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  color: #a0aec0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.icon-right {
  right: 12px;
}

.calendar-icon {
  /* Specific styles for calendar icon if any */
}

/* --- Deep Selectors for Datepicker Internals (if needed) --- */
/* Use :deep() to style child components from a scoped style block */
:deep(.dp__theme_light) {
  /* --dp-border-radius: 100px !important; */
  /* Example: --dp-input-padding: 0 40px 0 12px; Adjust if needed */
}

:deep(.dp__input) {
  --dp-border-radius: 100px !important;
}

:deep(.dp__action_button) {
  /* Style calendar buttons if needed */
}

:deep(.dp__calendar_header) {
  /* Style calendar header if needed */
}

/* vue-datepicker-next */
:deep(.dp__input_icon) {
  display: none !important;
}

/* vue3-datepicker */
:deep(.calendar-icon),
:deep(.datepicker-icon) {
  display: none !important;
}

/* Add other necessary styles that were specific to the datepicker */
</style>
